import { useQuery } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { queryKeys } from "./queryClient";

export interface Organization {
  idx: number;
  id: string;
  name: string;
  app_name: string;
  app_url: string;
  /** The organization's actual product/app URL (what referral links
   *  should point to) -- distinct from app_url, which is the affiliate
   *  dashboard's own domain. */
  base_url?: string;
  admin_email: string;
  commission_structure: {
    type: "percentage" | "fixed";
    default_rate: number;
  };
  settings: {
    auto_approve: boolean;
    minimum_payout: number;
    payout_delay_days: number;
    cookie_duration_days: number;
  };
  branding: {
    tagline: string;
    logo_url: string;
    primary_color: string;
  };
  created_at: string;
  updated_at: string;
  commission_rate: string;
  commission_type: string;
  min_payout_amount: string;
  payout_schedule: string;
  auto_approval_enabled: boolean;
  auto_approval_threshold: number;
  currency: string;
}

// Cache the organization in memory to avoid repeated fetches
let cachedOrg: Organization | null = null;

/**
 * Get organization based on current domain
 * Supports:
 * - Production domains (affiliate.willtheyconvert.com)
 * - Localhost with fallback to env variable
 */
export async function getCurrentOrganization(): Promise<Organization | null> {
  // Return cached if available (unless cache-busting parameter is present)
  const urlParams = new URLSearchParams(window.location.search);
  const forceRefresh = urlParams.get("refresh") === "true";

  if (cachedOrg && !forceRefresh) {
    return cachedOrg;
  }

  if (forceRefresh) {
    cachedOrg = null;
  }

  try {
    const hostname = window.location.hostname;
    const origin = window.location.origin;

    // Try to find by exact origin match first (without path)
    let { data: org, error } = await supabase
      .from("organizations_public")
      .select("*")
      .eq("app_url", origin)
      .single();

    // If not found, try matching by hostname (in case app_url includes or excludes path)
    if (error && hostname !== "localhost") {
      const { data: orgs, error: hostnameError } = await supabase
        .from("organizations_public")
        .select("*");

      if (!hostnameError && orgs && orgs.length > 0) {
        // Find org where app_url contains the current hostname
        org =
          orgs.find((o) => {
            try {
              const orgUrl = new URL(o.app_url);
              return orgUrl.hostname === hostname;
            } catch {
              return false;
            }
          }) || null;
        error = org ? null : error;
      }
    }

    // If not found and we're on localhost, try using environment variable
    if (error && hostname === "localhost") {
      const fallbackId = import.meta.env.VITE_ORGANIZATION_ID;

      if (fallbackId) {
        const { data: fallbackOrg, error: fallbackError } = await supabase
          .from("organizations_public")
          .select("*")
          .eq("id", fallbackId)
          .single();

        if (!fallbackError && fallbackOrg) {
          org = fallbackOrg;
          error = null;
        }
      }
    }

    if (error) {
      console.error("[Org Context] Error fetching organization:", error);
      // Clear cache on error to allow retry
      cachedOrg = null;
      return null;
    }

    if (!org) {
      console.error("[Org Context] No organization found for:", origin);
      // Clear cache on error to allow retry
      cachedOrg = null;
      return null;
    }

    // Cache it
    cachedOrg = org;
    return org;
  } catch (err) {
    console.error("[Org Context] Exception:", err);
    // Clear cache on exception to allow retry
    cachedOrg = null;
    return null;
  }
}

/**
 * React Query wrapper around getCurrentOrganization(). Every page
 * (AffiliateHeader, SettingsPage, AnalyticsPage, ProfessionalDashboard,
 * ProfessionalPayments) independently fetched the organization in its own
 * effect; the module-level `cachedOrg` only dedupes once the first call has
 * actually resolved, so several of those effects firing on the same
 * mount/navigation each issued their own request before any of them
 * finished. useQuery's built-in request dedup (same queryKey, concurrent
 * callers share one in-flight request) and cache (shared across every
 * component using this hook, surviving page navigation since it lives on
 * the QueryClient, not component state) fix both at once.
 */
export function useOrganization() {
  return useQuery({
    queryKey: queryKeys.organization,
    queryFn: getCurrentOrganization,
    staleTime: 5 * 60_000,
  });
}

/**
 * Get the current organization ID
 * Used throughout the app for filtering data
 */
export async function getCurrentOrgId(): Promise<string> {
  const org = await getCurrentOrganization();

  if (!org) {
    throw new Error(
      "No organization found for this domain. " +
        "Please ensure your domain is configured in the organizations table."
    );
  }

  return org.id;
}

/**
 * Clear the cached organization (useful for testing)
 */
export function clearOrgCache() {
  cachedOrg = null;
}

/**
 * Apply organization branding to the document
 */
export async function applyOrganizationBranding() {
  const org = await getCurrentOrganization();

  if (!org) {
    console.warn("[Org Branding] No organization found, skipping branding");
    return;
  }

  if (!org.branding) {
    console.warn(
      "[Org Branding] No branding data found for organization:",
      org.app_name
    );
    return;
  }

  // Set CSS custom properties for theming
  if (org.branding.primary_color) {
    document.documentElement.style.setProperty(
      "--primary-color",
      org.branding.primary_color
    );
  }

  // Set page title
  if (org.app_name) {
    document.title = org.app_name;
  }

  // Set favicon if provided
  if (org.branding.logo_url) {
    const favicon = document.querySelector(
      "link[rel*='icon']"
    ) as HTMLLinkElement;
    if (favicon) {
      favicon.href = org.branding.logo_url;
    }
  }
}
